/**
 * The frame of every admin page: the admin navigation, with what waits in each area, above the
 * page. Only the admin team sees it. The API checks admin rights on every admin request.
 */

import { NavLink, Outlet } from "react-router";

import { ADMIN_AREAS, useAdminQueues } from "../../admin/queues.js";
import { RequireAdmin } from "../../components/RequireAdmin.js";
import { styles } from "../../components/ui.js";

export function AdminLayout() {
  return (
    <RequireAdmin>
      <div className={styles.page}>
        <AdminNav />
        <div className="mt-8">
          <Outlet />
        </div>
      </div>
    </RequireAdmin>
  );
}

const tab =
  "flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium whitespace-nowrap transition";
const tabState = ({ isActive }: { isActive: boolean }) =>
  `${tab} ${
    isActive
      ? "bg-white text-zinc-950 shadow-[0_1px_2px_rgb(24_24_27/0.08),0_0_0_1px_rgb(24_24_27/0.06)] dark:bg-white/10 dark:text-white dark:shadow-none"
      : "text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white"
  }`;

function AdminNav() {
  const queues = useAdminQueues();
  return (
    <nav aria-label="Admin" className="-mx-4 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0">
      <ul className="inline-flex gap-0.5 rounded-xl bg-zinc-900/[0.04] p-1 ring-1 ring-zinc-900/[0.06] ring-inset dark:bg-white/[0.04] dark:ring-white/10">
        <li>
          <NavLink to="/admin" end className={tabState}>
            Overview
          </NavLink>
        </li>
        {ADMIN_AREAS.map((area) => {
          const waits = area.waiting(queues);
          return (
            <li key={area.to}>
              <NavLink to={area.to} className={tabState}>
                {area.label}
                {waits && (
                  <>
                    <span
                      aria-hidden
                      className={`rounded-full px-1.5 text-xs font-semibold tabular-nums ring-1 ring-inset ${
                        // News, such as new wallets, is blue. Work that waits is amber.
                        waits.news
                          ? "bg-brand-500/15 text-brand-800 ring-brand-500/25 dark:bg-brand-400/15 dark:text-brand-300 dark:ring-brand-400/25"
                          : "bg-amber-500/15 text-amber-700 ring-amber-500/25 dark:bg-amber-400/15 dark:text-amber-300 dark:ring-amber-400/25"
                      }`}
                    >
                      {area.to === "/admin/deployments" ? "!" : waits.count}
                    </span>
                    <span className="sr-only"> ({waits.text})</span>
                  </>
                )}
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
